import { User } from "features/User/models";
import moment from "moment";
import { Task } from "features/Resources/Task/models";
import { compactText, escapeHtml, makeListHtmlRaw } from "./html";

export type ReminderTemplate = "friendly" | "direct" | "explain_impact";

export const REMINDER_TEMPLATES: { value: ReminderTemplate; label: string; description: string }[] = [
  {
    value: "friendly",
    label: "Friendly",
    description: "Warm check-in, the everyday default.",
  },
  {
    value: "direct",
    label: "Direct",
    description: "Clear, neutral ask that is quick to the point.",
  },
  {
    value: "explain_impact",
    label: "Explain impact",
    description: "Adds context on why these tasks matter.",
  },
];

function formatDueDate(dueAt: string | Date): string {
  const due = moment(dueAt);
  const sameYear = due.year() === moment().year();
  return sameYear ? `due ${due.format("MMM D")}` : `due ${due.format("MMM D, YYYY")}`;
}

function statusForTask(t: Task): string {
  if (t.isOverdue) return "overdue";
  if (t.dueAt) return formatDueDate(t.dueAt);
  return "";
}

// Returns pre-built HTML strings (escaped names wrapped in <strong>) suitable for
// makeListHtmlRaw. All user-supplied values are escaped here; the raw list helper
// does not escape again.
function buildTaskItems(taskTitlesOrTasks: (string | Task)[]): string[] {
  const tasks = (taskTitlesOrTasks || []).filter((t): t is Task => typeof t !== "string");
  const taskTitles = (taskTitlesOrTasks || []).filter((t): t is string => typeof t === "string");

  const sortedTasks = [...tasks].sort((a, b) => {
    const aStart = a.effectiveStartDate ? new Date(a.effectiveStartDate).getTime() : 0;
    const bStart = b.effectiveStartDate ? new Date(b.effectiveStartDate).getTime() : 0;
    return bStart - aStart; // newest start date first
  });

  const formatTask = (t: Task) => {
    const name = `<strong>${escapeHtml(t.name)}</strong>`;
    const status = statusForTask(t);
    return status ? `${name} (${escapeHtml(status)})` : name;
  };

  const overdueTasks = sortedTasks.filter((t) => t.isOverdue).map(formatTask);
  const regularTasks = sortedTasks.filter((t) => !t.isOverdue).map(formatTask);

  return [
    ...overdueTasks,
    ...regularTasks,
    ...taskTitles.map((t) => `<strong>${escapeHtml(t)}</strong>`),
  ];
}

// Journey-context phrase for reminder copy.
//   * assignee is the new hire     → "your onboarding"
//   * new-hire name known           → "<name>'s onboarding"
//   * neither                       → "this onboarding"
function contextPhrase(assignee: User | undefined, newHireName?: string, newHireId?: string): string {
  if (assignee?.id && newHireId && assignee.id === newHireId) return "your onboarding";
  if (newHireName) return `${escapeHtml(newHireName)}'s onboarding`;
  return "this onboarding";
}

function friendlyTemplate(firstName: string, taskList: string, url: string, context: string): string {
  return compactText(`
    <p>Hi ${firstName},</p>
    <p>&nbsp;</p>
    <p>Just a friendly reminder about ${context}. You have some tasks that need attention. Let me know if you need any help!</p>
    <p>&nbsp;</p>
    ${taskList}
    <p>&nbsp;</p>
    ${url ? `<p>Complete your tasks here: ${url}</p>` : ""}
    <p>&nbsp;</p>
    <p>Thanks! Feel free to reach out with any questions.</p>
  `);
}

function directTemplate(firstName: string, taskList: string, url: string, context: string): string {
  return compactText(`
    <p>Hi ${firstName},</p>
    <p>&nbsp;</p>
    <p>Could you wrap up these tasks for ${context} asap? Let me know if anything's blocking you.</p>
    <p>&nbsp;</p>
    ${taskList}
    <p>&nbsp;</p>
    ${url ? `<p>Complete your tasks here: ${url}</p>` : ""}
  `);
}

function explainImpactTemplate(firstName: string, taskList: string, url: string, context: string): string {
  return compactText(`
    <p>Hi ${firstName},</p>
    <p>&nbsp;</p>
    <p>These tasks are part of ${context} and are key to a smooth start. Your help makes a real difference. Wanted to flag these in case they slipped through.</p>
    <p>&nbsp;</p>
    ${taskList}
    <p>&nbsp;</p>
    ${url ? `<p>Complete your tasks here: ${url}</p>` : ""}
    <p>&nbsp;</p>
    <p>Thanks! Your efforts here support the culture we are building!</p>
  `);
}

export default function generateReminderBodyForTasks(
  assignee: User | undefined,
  taskTitlesOrTasks: (string | Task)[],
  resolveUrl?: string,
  template: ReminderTemplate = "friendly",
  newHireName?: string,
  newHireId?: string,
): string {
  const firstName = escapeHtml(assignee?.firstName || "there");
  const items = buildTaskItems(taskTitlesOrTasks);
  const taskList = makeListHtmlRaw(items);
  const url = resolveUrl ? escapeHtml(resolveUrl) : "";
  const context = contextPhrase(assignee, newHireName, newHireId);

  if (template === "friendly") return friendlyTemplate(firstName, taskList, url, context);
  if (template === "explain_impact") return explainImpactTemplate(firstName, taskList, url, context);
  return directTemplate(firstName, taskList, url, context);
}
